import { useEffect, useState } from "react";
import { Award, Edit3, X } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { api } from "../lib/apiClient";
import { useApp } from "../lib/store";
import { PageHeader, GradeBadge, Avatar, Empty, StatCard } from "../components/Shared";
import { useEmployees, EmployeePicker, Panel } from "../components/Filters";
import { scoreColor } from "../lib/constants";
import { toast } from "sonner";

export default function Yearly() {
  const { year, can, isEmployee, selfEmployeeId } = useApp();
  const employees = useEmployees();
  const [empId, setEmpId] = useState(isEmployee ? selfEmployeeId : "om");
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(false);

  const load = () => { if (empId) api.yearly(empId, year).then(setData).catch(() => {}); };
  useEffect(() => { load(); }, [empId, year]);

  if (!data) return <div className="py-20 text-center text-muted-c">Loading…</div>;
  const monthly = data.monthly.map((m) => ({ month: m.month_name.slice(0, 3), score: m.score }));
  const quarters = data.quarters.map((q) => ({ q: `Q${q.quarter}`, score: q.score }));

  return (
    <div>
      <PageHeader title="Yearly Review & Increment" subtitle={`FY ${year} · automatic increment recommendation`}
        exportModule="yearly" exportParams={{ employee_id: empId, year }}>
        <EmployeePicker value={empId} onChange={setEmpId} employees={employees} disabled={isEmployee} testid="yearly-employee-picker" />
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="card-panel p-5 flex items-center gap-3 fade-up">
          <Avatar src={data.employee.profile_image} name={data.employee.name} size={48} />
          <div><p className="font-display font-bold">{data.employee.name}</p><p className="text-xs text-secondary">{data.employee.designation}</p></div>
        </div>
        <StatCard label="Yearly Score" value={data.yearly_score} sub={`Grade ${data.grade}`} icon={Award} accent={scoreColor(data.yearly_score)} testid="yearly-score-card" />
        <StatCard label="Recommended Increment" value={`${data.recommended_increment}%`} sub="auto-calculated" accent="#2563EB" />
        <div className="card-panel p-5 fade-up" data-testid="final-increment-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-muted-c">Final Increment</p>
              <p className="kpi-value text-2xl sm:text-3xl mt-2 brand-text">{data.final_increment}%</p>
              <p className="text-xs text-secondary mt-1.5">{data.override ? "Overridden by management" : "Matches recommendation"}</p>
            </div>
            {can.overrideIncrement && <button onClick={() => setModal(true)} className="w-9 h-9 rounded-lg elevated border border-subtle flex items-center justify-center hover:brand-text" data-testid="override-increment-button"><Edit3 size={16} /></button>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
        <div className="lg:col-span-8">
          <Panel title="Month-wise Performance Trend" testid="yearly-trend">
            {monthly.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={monthly} margin={{ top: 10, right: 20, left: -20, bottom: 0 }}>
                  <defs><linearGradient id="yg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity={0.4} /><stop offset="100%" stopColor="#2563EB" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false} />
                  <XAxis dataKey="month" tick={{ fill: "#64748B", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fill: "#64748B", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 10 }} />
                  <Area type="monotone" dataKey="score" stroke="#2563EB" strokeWidth={2.5} fill="url(#yg)" isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <Empty />}
          </Panel>
        </div>
        <div className="lg:col-span-4">
          <Panel title="Quarter-wise Performance" testid="yearly-quarters">
            {quarters.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={quarters} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false} />
                  <XAxis dataKey="q" tick={{ fill: "#64748B", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fill: "#64748B", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "rgba(37,99,235,0.08)" }} contentStyle={{ background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 10 }} />
                  <Bar dataKey="score" radius={[6, 6, 0, 0]} maxBarSize={50} isAnimationActive={false}>{quarters.map((q, i) => <Cell key={i} fill={scoreColor(q.score)} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty />}
          </Panel>
        </div>
      </div>

      {data.override && (
        <Panel title="Override Audit" testid="override-audit">
          <p className="text-sm"><span className="text-muted-c">Reason:</span> {data.override.reason}</p>
          {data.override.manager_remarks && <p className="text-sm mt-1"><span className="text-muted-c">Remarks:</span> {data.override.manager_remarks}</p>}
        </Panel>
      )}

      {modal && <OverrideModal empId={empId} year={year} current={data.final_increment} onClose={() => setModal(false)} onSaved={() => { setModal(false); load(); }} />}
    </div>
  );
}

function OverrideModal({ empId, year, current, onClose, onSaved }) {
  const [val, setVal] = useState(current);
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const save = async () => {
    if (!reason) return toast.error("A reason is required for override");
    await api.incrementOverride({ employee_id: empId, year, override_increment: Number(val), reason, manager_remarks: remarks });
    toast.success("Increment override saved to audit history"); onSaved();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
      <div className="card-panel w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="override-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-subtle"><h3 className="font-display font-semibold">Override Increment</h3><button onClick={onClose}><X size={18} className="text-muted-c" /></button></div>
        <div className="p-5 space-y-3">
          <div><label className="text-xs font-semibold text-muted-c uppercase block mb-1.5">Increment %</label><input type="number" className="input-c mono" value={val} onChange={(e) => setVal(e.target.value)} data-testid="increment-recommendation-input" /></div>
          <div><label className="text-xs font-semibold text-muted-c uppercase block mb-1.5">Reason (required)</label><input className="input-c" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why override?" data-testid="override-reason-input" /></div>
          <div><label className="text-xs font-semibold text-muted-c uppercase block mb-1.5">Manager Remarks</label><textarea rows={2} className="input-c" value={remarks} onChange={(e) => setRemarks(e.target.value)} /></div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-subtle"><button onClick={onClose} className="pill px-4 py-2 text-sm border-strong">Cancel</button><button onClick={save} className="pill px-4 py-2 text-sm brand-bg text-white font-semibold" data-testid="save-override-button">Save Override</button></div>
      </div>
    </div>
  );
}
